import { FunctionComponent, useState, useEffect, useRef, useMemo } from 'react';
import DashboardLayout from '../../components/layout/layout';
import { Grid,Box, Button, Alert, Paper, Skeleton, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, List, ListItem, ListItemText, ClickAwayListener, Tooltip } from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useGetInventory, useGetInventoryAutocomplete } from '../../hooks/api/user';
import moment from 'moment';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { StyledTextField } from '../../components/ui/formInput';

const TableCol = styled(TableCell)(({ theme }) => ({
    border: '1px solid rgba(224, 224, 224, 1)',
}));


type InventoryProps = {
    partNo: string;
    freshDate: string;
    qty: number;
    repName: string;
    repLocation: string;
    nodash: string;
    worktable: string;
}

type ValidateField = {
    invalid: boolean;
    error: string;
};

type ValidateFieldsState = {
    partNumber: ValidateField;
};

type SortField = 'partNo' | 'freshDate' | 'qty' | 'repName';
type SortDirection = 'asc' | 'desc' | null;




const InventorySearchResult: FunctionComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const partNo = queryParams.get('partNo') || '';
    const [partNumber, setPartNumber] = useState<string>(partNo);
    const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
    const [debouncedPrefix, setDebouncedPrefix] = useState<string>('');
    const autocompleteRef = useRef<HTMLDivElement>(null);
    
    // Get all inventory data without pagination
    const { data: inventory, isFetched: hasFetched, error } = useGetInventory({ partNo });
    const allInventoryList = useMemo(() => {
        return Array.isArray(inventory?.data) ? inventory.data : [];
    }, [inventory?.data]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [suggestion, setSuggestion] = useState<string | null>(null);

    const formValidation: ValidateFieldsState = {
        partNumber: { invalid: false, error: '' }
    };
    const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

    // Sorting state (pagination removed)
    const [sortField, setSortField] = useState<SortField>('partNo');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Sort all data client-side
    const sortedData = useMemo(() => {
        if (!allInventoryList.length) return [];
        
        return [...allInventoryList].sort((a, b) => {
            let aValue: any = a[sortField];
            let bValue: any = b[sortField];

            // Handle date sorting
            if (sortField === 'freshDate') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }
            // Handle number sorting
            else if (sortField === 'qty') {
                aValue = Number(aValue);
                bValue = Number(bValue);
            }
            // Handle string sorting (case-insensitive)
            else {
                aValue = String(aValue).toLowerCase();
                bValue = String(bValue).toLowerCase();
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });
    }, [allInventoryList, sortField, sortDirection]);

    // Use all sorted data (no pagination)
    const displayData = useMemo(() => {
        return sortedData;
    }, [sortedData]);

    // Handle sorting
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Autocomplete query - only when user is actively typing (partNumber differs from partNo)
    const { data: autocompleteData, isLoading: isAutocompleteLoading } = useGetInventoryAutocomplete(
        partNumber !== partNo ? debouncedPrefix : '' // Only search when user is typing something different
    );
    const suggestions = autocompleteData?.partNumbers || [];


   

    useEffect(() => {
        if (error) {
            if (axios.isAxiosError(error) && error.response && error.response.data) {
                const errData = error.response.data as { message?: string; suggestion?: string };
                setErrorMessage(errData.message || 'An error occurred');
                setSuggestion(errData.suggestion || null);
            } else if (typeof error === 'string') {
                setErrorMessage(error);
                setSuggestion(null);
            } else {
                setErrorMessage('An unknown error occurred');
                setSuggestion(null);
            }
        } else {
            setErrorMessage(null);
            setSuggestion(null);
        }

    }, [error, inventory]);

    useEffect(() => {
        setPartNumber(partNo);
    }, [partNo]);

    // Debounce effect for autocomplete - only when user is actively typing
    useEffect(() => {
        // Don't show autocomplete if user is not typing something different
        if (partNumber === partNo) {
            setShowAutocomplete(false);
            return;
        }
        
        const timer = setTimeout(() => {
            if (partNumber.trim().length >= 2) {
                setDebouncedPrefix(partNumber.trim());
                setShowAutocomplete(true);
            } else {
                setShowAutocomplete(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [partNumber, partNo]);

    // Handle suggestion selection - auto-search
    const handleSuggestionClick = (suggestion: string) => {
        setPartNumber(suggestion);
        setShowAutocomplete(false);
        // Auto-trigger search
        setTimeout(() => {
            navigate(`/inventory/inventory-search-result?partNo=${encodeURIComponent(suggestion)}`);
        }, 100); // Small delay to ensure state is updated
    };

    // Handle input change
    const handlePartNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPartNumber(value);
        setValidateFields(formValidation); // Clear validation errors
    };

    // Handle click away from autocomplete
    const handleClickAway = () => {
        setShowAutocomplete(false);
    };

    const queryClient = useQueryClient();

    const handleBack = () => {
        queryClient.clear();
        navigate('/inventory');
    };

    // Get sort icon for a column
    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <UnfoldMoreIcon fontSize="small" />;
        }
        if (sortDirection === 'asc') {
            return <ArrowUpwardIcon fontSize="small" />;
        }
        if (sortDirection === 'desc') {
            return <ArrowDownwardIcon fontSize="small" />;
        }
        return <UnfoldMoreIcon fontSize="small" />;
    };

    const handleAction = (e?: React.FormEvent<HTMLFormElement>) => {
        if (e) e.preventDefault();
        setValidateFields(formValidation);
        if (!validateForm()) {
            return;
        }
        const trimmed = partNumber.trim();
        navigate(`/inventory/inventory-search-result?partNo=${encodeURIComponent(trimmed)}`);
    };

    const validateForm = () => {
        let valid = true;
        const errors = { ...validateFields };

        if (!partNumber?.trim()) {
            errors.partNumber = { invalid: true, error: 'Part number is required' };
            valid = false;
        } else {
            errors.partNumber = { invalid: false, error: '' };
        }

        setValidateFields(errors);
        return valid;
    };


    return (
        <>
            <DashboardLayout titleIcon={<LockOutlinedIcon />} title={"Inventory"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={''} breadcrumbs={' Inventory Search'} activeMenu={'inventory'}>
                <Box sx={{ width: '100%', maxWidth: '100%' }}>
                    <Grid>
                        <Grid>
                            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
                                Barksdale Part Number: {partNo}
                            </Typography>
                        </Grid>
                        <form onSubmit={handleAction} >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                            <Box sx={{ position: 'relative', flex: 1 }}>
                                <StyledTextField
                                    label="Enter Part Number"
                                    value={partNumber}
                                    onChange={handlePartNumberChange}
                                    error={validateFields.partNumber.invalid}
                                    helperText={validateFields.partNumber.error}
                                />
                                {showAutocomplete && (
                                    <ClickAwayListener onClickAway={handleClickAway}>
                                        <Paper
                                            ref={autocompleteRef}
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                maxHeight: 200,
                                                overflow: 'auto',
                                                boxShadow: 3,
                                                border: '1px solid #ddd',
                                                mt: 0.5
                                            }}
                                        >
                                            <List dense>
                                                {isAutocompleteLoading ? (
                                                    <ListItem>
                                                        <ListItemText primary="Loading..." />
                                                    </ListItem>
                                                ) : suggestions.length > 0 ? (
                                                    suggestions.map((suggestion: string, index: number) => (
                                                        <ListItem
                                                            key={index}
                                                            component="div"
                                                            onClick={() => handleSuggestionClick(suggestion)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    backgroundColor: '#f5f5f5'
                                                                }
                                                            }}
                                                        >
                                                            <ListItemText primary={suggestion} />
                                                        </ListItem>
                                                    ))
                                                ) : (
                                                    <ListItem>
                                                        <ListItemText primary="No suggestions found" />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </Paper>
                                    </ClickAwayListener>
                                )}
                            </Box>
                            <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} type="submit">
                                SEARCH
                            </Button>
                        </Box>
                        </form>
                    </Grid>

                    <Box sx={{ mt: 5 }}>
                        {errorMessage && (
                            <Box sx={{ mt: 2 }}>
                                <Alert severity="error">
                                    {errorMessage}
                                    {suggestion && (
                                        <>
                                            <br />
                                            {suggestion}
                                        </>
                                    )}
                                </Alert>
                            </Box>
                        )}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 3 }}>
                            <Table size="small" aria-label="inventory list table">
                                <TableHead sx={{ backgroundColor: "#c3183c" }}>
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ color: 'white' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ color: 'white' }}>
                                                    Barksdale Part Number: {partNo}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'white' }}>
                                                    Results: {allInventoryList.length}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                                    <TableRow>
                                        {/* <TableCol>Sr. no</TableCol> */}
                                        <TableCol 
                                            sx={{ 
                                                cursor: 'pointer', 
                                                '&:hover': { backgroundColor: '#e0e0e0' },
                                                userSelect: 'none'
                                            }}
                                            onClick={() => handleSort('partNo')}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                Part No
                                                <Tooltip title="Click to sort">
                                                    {getSortIcon('partNo')}
                                                </Tooltip>
                                            </Box>
                                        </TableCol>
                                        <TableCol 
                                            sx={{ 
                                                cursor: 'pointer', 
                                                '&:hover': { backgroundColor: '#e0e0e0' },
                                                userSelect: 'none'
                                            }}
                                            onClick={() => handleSort('freshDate')}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                Date
                                                <Tooltip title="Click to sort">
                                                    {getSortIcon('freshDate')}
                                                </Tooltip>
                                            </Box>
                                        </TableCol>
                                        <TableCol 
                                            sx={{ 
                                                cursor: 'pointer', 
                                                '&:hover': { backgroundColor: '#e0e0e0' },
                                                userSelect: 'none'
                                            }}
                                            onClick={() => handleSort('qty')}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                In Stock
                                                <Tooltip title="Click to sort">
                                                    {getSortIcon('qty')}
                                                </Tooltip>
                                            </Box>
                                        </TableCol>
                                        <TableCol 
                                            sx={{ 
                                                cursor: 'pointer', 
                                                '&:hover': { backgroundColor: '#e0e0e0' },
                                                userSelect: 'none'
                                            }}
                                            onClick={() => handleSort('repName')}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                Company
                                                <Tooltip title="Click to sort">
                                                    {getSortIcon('repName')}
                                                </Tooltip>
                                            </Box>
                                        </TableCol>
                                        <TableCol>Details</TableCol>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        hasFetched ? (
                                            displayData.length === 0 ? (
                                                <TableRow>
                                                    <TableCol colSpan={6} sx={{ textAlign: 'center' }}>
                                                        {/* No Data Found */}
                                                        <Typography variant="body2" sx={{ color: 'black' }}>
                                                        Unfortunately this item is not available from stock at this time.
                                                        </Typography>
                                                    </TableCol>
                                                </TableRow>
                                            ) : (
                                                displayData.map((inventory: InventoryProps, idx: number) => (
                                                    //: inventory.map((inventory: InventoryProps, idx: number) => (
                                                    <TableRow key={idx + 1} hover>
                                                        {/* <TableCell>{(page - 1) * size + idx + 1}.</TableCell> */}
                                                        <TableCol>{inventory.partNo}</TableCol>
                                                        <TableCol>{moment(inventory.freshDate).format('MM/DD/YYYY')}</TableCol>
                                                        <TableCol>{inventory.qty}</TableCol>
                                                        <TableCol>{inventory.repName}</TableCol>
                                                        <TableCol>
                                                            <Link
                                                                to={`/inventory/details/${inventory.repName}/${inventory.partNo}`}
                                                                style={{
                                                                    textDecoration: "underline",
                                                                    color: "#c3183c",
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                See Details
                                                            </Link>
                                                        </TableCol>
                                                    </TableRow>
                                                ))
                                            )
                                        ) : (
                                            <>
                                                <TableRow>
                                                    <TableCol colSpan={11}>
                                                        <Skeleton width="38%" />
                                                        <Skeleton width="100%" />
                                                        <Skeleton width="100%" />
                                                        <Skeleton width="61%" />
                                                    </TableCol>
                                                </TableRow>
                                            </>
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                mt: 3,
                                display: "flex",
                                justifyContent: "flex-start"
                            }}
                        >
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: '#c3183c', padding: '7px 25px !important' }}
                                onClick={handleBack}
                            >
                                BACK
                            </Button>
                        </Box>

                    </Box>
                </Box>
            </DashboardLayout>
        </>
    )

};
export default InventorySearchResult;